import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';

// Animated Counter Component
export const AnimatedCounter = ({ 
  value, 
  suffix = '', 
  prefix = '',
  duration = 2,
  className = '' 
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      const targetValue = parseInt(value.replace(/\D/g, '')) || 0;
      const steps = 60;
      const increment = targetValue / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= targetValue) {
          setCount(targetValue);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, (duration * 1000) / steps);

      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);

  // Extract the suffix from original value (like + or K)
  const originalSuffix = value.replace(/[\d.]/g, '');

  return (
    <span ref={ref} className={className}>
      {prefix}{count}{originalSuffix || suffix}
    </span>
  );
};

// Mission Stat Card with tactical styling
export const MissionStatCard = ({ 
  value, 
  label, 
  icon: Icon,
  delay = 0,
  className = '' 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={`relative group ${className}`}
    >
      {/* Background with tactical corners */}
      <div className="relative p-6 bg-vav-bg-paper/80 backdrop-blur-sm border border-vav-border rounded-xl overflow-hidden transition-all duration-300 group-hover:border-vav-gold/50">
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-vav-gold/30 group-hover:border-vav-gold transition-colors" />
        <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-vav-gold/30 group-hover:border-vav-gold transition-colors" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-vav-gold/30 group-hover:border-vav-gold transition-colors" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-vav-gold/30 group-hover:border-vav-gold transition-colors" />

        {/* Animated scan line */}
        <motion.div
          className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-vav-gold to-transparent"
          initial={{ x: '-100%' }}
          animate={isInView ? { x: '100%' } : {}}
          transition={{ duration: 1.5, delay: delay + 0.3, ease: "easeInOut" }}
        />

        {/* Content */}
        <div className="relative text-center">
          {Icon && (
            <Icon className="w-8 h-8 mx-auto mb-3 text-vav-gold/70 group-hover:text-vav-gold transition-colors" />
          )}
          <div className="font-display text-4xl sm:text-5xl text-vav-gold mb-2">
            <AnimatedCounter value={value} duration={1.5} />
          </div>
          <div className="text-xs text-vav-fg-muted uppercase tracking-wider font-mono">
            {label}
          </div>
        </div>

        {/* Glow effect on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-t from-vav-gold/5 to-transparent" />
      </div>
    </motion.div>
  );
};

// Tactical Text Reveal Animation
export const TacticalTextReveal = ({ 
  text, 
  delay = 0,
  className = '' 
}) => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.03,
        delayChildren: delay,
      }
    }
  };

  const letterVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      rotateX: -90
    },
    visible: { 
      opacity: 1, 
      y: 0,
      rotateX: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 200
      }
    }
  };

  return (
    <motion.span
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={controls}
      className={`inline-block ${className}`}
    >
      {text.split('').map((char, index) => (
        <motion.span
          key={index}
          variants={letterVariants}
          className="inline-block"
          style={{ whiteSpace: char === ' ' ? 'pre' : 'normal' }}
        >
          {char}
        </motion.span>
      ))}
    </motion.span>
  );
};

// Glitch Text Effect
export const GlitchText = ({ 
  text, 
  className = '' 
}) => {
  const [isGlitching, setIsGlitching] = useState(false);

  useEffect(() => {
    // Random glitch every 3-7 seconds
    const interval = setInterval(() => {
      setIsGlitching(true);
      setTimeout(() => setIsGlitching(false), 200);
    }, Math.random() * 4000 + 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <span className={`relative inline-block ${className}`}>
      <span className="relative z-10">{text}</span>
      {isGlitching && (
        <>
          <span 
            className="absolute inset-0 text-vav-gold/80"
            style={{ 
              clipPath: 'inset(20% 0 40% 0)',
              transform: 'translate(-2px, 0)'
            }}
          >
            {text}
          </span>
          <span 
            className="absolute inset-0 text-red-500/50"
            style={{ 
              clipPath: 'inset(60% 0 10% 0)',
              transform: 'translate(2px, 0)'
            }}
          >
            {text}
          </span>
        </>
      )}
    </span>
  );
};

// Scroll-triggered Reveal
export const ScrollReveal = ({ 
  children, 
  direction = 'up',
  delay = 0,
  className = '' 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const directions = {
    up: { y: 40 },
    down: { y: -40 },
    left: { x: 40 },
    right: { x: -40 }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ 
        opacity: 0, 
        ...directions[direction]
      }}
      animate={isInView ? { 
        opacity: 1, 
        x: 0, 
        y: 0 
      } : {}}
      transition={{ 
        duration: 0.8, 
        delay,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Tactical Loading Spinner
export const TacticalSpinner = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16'
  };

  return (
    <div className={`relative ${sizes[size]} ${className}`}>
      {/* Outer ring */}
      <motion.div
        className="absolute inset-0 border-2 border-vav-gold/30 rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
      />
      {/* Inner ring */}
      <motion.div
        className="absolute inset-1 border-2 border-t-vav-gold border-r-transparent border-b-transparent border-l-transparent rounded-full"
        animate={{ rotate: -360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
      {/* Center dot */}
      <motion.div
        className="absolute top-1/2 left-1/2 w-2 h-2 -translate-x-1/2 -translate-y-1/2 bg-vav-gold rounded-full"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 1, repeat: Infinity }}
      />
    </div>
  );
};

// Tactical Progress Bar
export const TacticalProgress = ({ 
  value = 0, 
  label = '',
  showValue = true,
  className = '' 
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <div ref={ref} className={`space-y-2 ${className}`}>
      {(label || showValue) && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-vav-fg-muted font-mono uppercase tracking-wider">
            {label}
          </span>
          {showValue && (
            <span className="text-sm text-vav-gold font-mono">
              {value}%
            </span>
          )}
        </div>
      )}
      <div className="relative h-1 bg-vav-bg-subtle rounded-full overflow-hidden">
        <motion.div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-vav-gold to-vav-accent-green rounded-full"
          initial={{ width: 0 }}
          animate={isInView ? { width: `${value}%` } : {}}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
        {/* Shine effect */}
        <motion.div
          className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent"
          initial={{ x: '-100%' }}
          animate={isInView ? { x: '200%' } : {}}
          transition={{ duration: 2, delay: 0.5 }}
        />
      </div>
    </div>
  );
};

export default {
  AnimatedCounter,
  MissionStatCard,
  TacticalTextReveal,
  GlitchText,
  ScrollReveal,
  TacticalSpinner,
  TacticalProgress
};
